import { z } from "zod";
import {
  ACCOUNT_BUCKETS,
  CONTACT_BUCKETS,
  ACCOUNT_KINDS,
  ACTIVITY_STATUSES,
  ACTIVITY_BUCKETS,
  ACTIVITY_TYPES,
  CALL_DIRECTIONS,
  COMPANY_SIZES,
  DEAL_RESULTS,
  DEAL_STAGES,
  LEAD_BUCKETS,
  LEAD_RATINGS,
  LEAD_STATUSES,
  MEETING_PROVIDERS,
  MESSAGE_CHANNELS,
  PRIORITIES,
} from "./crm.js";
import { AvatarFieldSchema } from "./avatar.js";
import { ColumnFiltersParamSchema } from "./columnFilters.js";

/**
 * مخططات التحقق مشتركة بين الخادم والواجهة.
 *
 * المخطط الواحد يعطي قاعدة التحقق ونوع TypeScript معًا، فلا يمكن أن يتخلّف
 * أحدهما عن الآخر. ومشاركته مع الواجهة تعني أن الرسالة التي يراها المستخدم
 * قبل الإرسال هي نفسها التي يفرضها الخادم بعده.
 */

/* ==========================================================================
   أدوات مشتركة
   ========================================================================== */

/** نص اختياري: الفراغ يُحوَّل إلى null بدل أن يُخزَّن كسلسلة فارغة. */
const optionalText = (max = 500) =>
  z
    .string()
    .trim()
    .max(max, `النص أطول من ${max} حرفًا.`)
    .transform((v) => (v === "" ? null : v))
    .nullable()
    .optional();

const optionalEmail = z
  .string()
  .trim()
  .toLowerCase()
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional()
  .refine(
    (v) =>
      v === null || v === undefined || z.string().email().safeParse(v).success,
    {
      message: "صيغة البريد الإلكتروني غير صحيحة.",
    },
  );

/** رقم هاتف: نقبل الأرقام والمسافات والرموز الشائعة دون فرض صيغة بلد بعينها. */
const optionalPhone = z
  .string()
  .trim()
  .max(32, "رقم الهاتف أطول من اللازم.")
  .transform((v) => (v === "" ? null : v))
  .nullable()
  .optional()
  .refine(
    (v) => v === null || v === undefined || /^[\d\s+()-]{6,32}$/.test(v),
    {
      message:
        "رقم الهاتف يحتوي على رموز غير مقبولة، أو أنه بصيغة علمية فقد خاناته.",
    },
  )
  /*
   * الفواصل تُنزع قبل الحفظ.
   *
   * الرقم نفسه مكتوبًا بمسافات مرة وبشرطات أخرى يصير رقمين لا يجمعهما
   * بحث ولا تكشف تكرارهما مقارنة.
   */
  .transform((v) => (v == null ? v : v.replace(/[\s()-]/g, "")));

const money = z
  .number()
  .min(0, "القيمة لا يمكن أن تكون سالبة.")
  .max(99_999_999_999.99, "القيمة أكبر من الحد المسموح.");

const optionalMoney = money.nullable().optional();

/**
 * قيم الحقول المخصَّصة.
 *
 * سجلّ مفتوح هنا لأن المفاتيح تعرَّف في قاعدة البيانات لا في الشيفرة،
 * والتحقق الفعلي يقع في الخدمة على تعريفات المستأجر: المفتاح غير المعرَّف
 * يُرفض هناك، ولا يُقبل شيء لم تعرّفه الشركة.
 */
const CustomFieldsBag = z.record(
  z.union([z.string(), z.number(), z.boolean(), z.null()]),
);

const optionalDate = z.coerce.date().nullable().optional();

export const CuidSchema = z
  .string()
  .regex(/^[a-z0-9]{20,32}$/i, "معرّف غير صالح.");

/* ==========================================================================
   استعلام القوائم
   ========================================================================== */

export const ListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
  /** بحث نصي حر على الحقول الأساسية للسجل. */
  q: z.string().trim().max(120).optional(),
  sort: z.string().trim().max(40).optional(),
  order: z.enum(["asc", "desc"]).default("desc"),
  /** شروط الأعمدة من زرّ «فلتر» — انظر `columnFilters.ts`. */
  f: ColumnFiltersParamSchema,
});
export type ListQuery = z.infer<typeof ListQuerySchema>;

export interface Page<T> {
  items: T[];
  total: number;
  page: number;
  pageSize: number;
  pageCount: number;
}

/* ==========================================================================
   العملاء المحتملون
   ========================================================================== */

export const LeadListQuerySchema = ListQuerySchema.extend({
  status: z.enum(LEAD_STATUSES).optional(),
  rating: z.enum(LEAD_RATINGS).optional(),
  source: z.string().trim().max(80).optional(),
  ownerId: CuidSchema.optional(),
  /** دلو النظرة السريعة — انظر `LEAD_BUCKETS`. */
  bucket: z.enum(LEAD_BUCKETS).optional(),
  /** غير المحوَّلين وغير المستبعدين فقط. */
  openOnly: z.coerce.boolean().optional(),
});
export type LeadListQuery = z.infer<typeof LeadListQuerySchema>;

export const CreateLeadSchema = z.object({
  name: z.string().trim().min(2, "الاسم مطلوب.").max(160),
  company: optionalText(160),
  jobTitle: optionalText(120),
  email: optionalEmail,
  phone: optionalPhone,
  altPhone: optionalPhone,
  /* معرّفا التواصل الاجتماعي — تُخزَّن بلا @ في أولها. */
  messengerHandle: optionalText(120),
  instagramHandle: optionalText(120),
  /* توجيه القنوات: أيُّ الرقمين يستقبل واتساب وأيُّهما يستقبل SMS. */
  waPhone: z.enum(["phone", "altPhone"]).optional(),
  smsPhone: z.enum(["phone", "altPhone"]).optional(),
  /* رابط موقع العميل على الخرائط — يظهر مع الاجتماعات الحضورية. */
  mapsUrl: optionalText(500),
  source: optionalText(80),
  industry: optionalText(80),
  city: optionalText(80),
  address: optionalText(300),
  notes: optionalText(2000),
  /* الصورة `data:` مُصغَّرة — الحدّ والصيغ في `avatar.ts`. */
  avatarUrl: AvatarFieldSchema,
  status: z.enum(LEAD_STATUSES).default("NEW"),
  rating: z.enum(LEAD_RATINGS).default("WARM"),
  value: optionalMoney,
  ownerId: CuidSchema.nullable().optional(),
  nextActionAt: optionalDate,
  customFields: CustomFieldsBag.optional(),
});
export type CreateLeadInput = z.infer<typeof CreateLeadSchema>;

export const UpdateLeadSchema = CreateLeadSchema.partial();
export type UpdateLeadInput = z.infer<typeof UpdateLeadSchema>;

/* ==========================================================================
   الإجراءات الجماعية
   ========================================================================== */

export const BULK_LEAD_ACTIONS = [
  "DELETE",
  "STATUS",
  "OWNER",
  "CONVERT",
] as const;
export type BulkLeadAction = (typeof BULK_LEAD_ACTIONS)[number];

/**
 * سقف العناصر في الطلب الواحد.
 *
 * التحويل يُنشئ حسابًا وصفقة لكل عنصر، فمئتان تعني ستّ مئة كتابة في طلب
 * واحد. والسقف يجعل الطلب ينتهي في وقت معقول ويُبقي الرسالة مفهومة.
 */
export const MAX_BULK_ITEMS = 200;

export const BulkLeadsSchema = z
  .object({
    ids: z
      .array(CuidSchema)
      .min(1, "لم تُحدَّد عناصر.")
      .max(MAX_BULK_ITEMS, `الحد ${MAX_BULK_ITEMS} عنصرًا في المرة الواحدة.`),
    action: z.enum(BULK_LEAD_ACTIONS),
    status: z.enum(LEAD_STATUSES).optional(),
    ownerId: CuidSchema.nullable().optional(),
    /** للتحويل: إنشاء صفقة مع كل حساب. */
    createDeal: z.boolean().default(false),
  })
  .refine((v) => v.action !== "STATUS" || v.status !== undefined, {
    message: "اختر الحالة الجديدة.",
    path: ["status"],
  })
  .refine((v) => v.action !== "OWNER" || v.ownerId !== undefined, {
    message: "اختر المسؤول.",
    path: ["ownerId"],
  });
export type BulkLeadsInput = z.infer<typeof BulkLeadsSchema>;

/** نتيجة عنصر أخفق، باسمه وسببه. */
export interface BulkFailure {
  id: string;
  name: string;
  reason: string;
}

export interface BulkResult {
  total: number;
  succeeded: number;
  failed: number;
  failures: BulkFailure[];
}

export const ConvertLeadSchema = z.object({
  /** نوع الحساب الناتج. الافتراضي شركة إن كان للعميل اسم شركة. */
  accountKind: z.enum(ACCOUNT_KINDS).optional(),
  accountName: z.string().trim().min(2).max(160).optional(),
  /** الربط بحساب قائم بدل إنشاء حساب جديد، لتفادي التكرار. */
  existingAccountId: CuidSchema.optional(),
  /** إنشاء صفقة مع التحويل. */
  createDeal: z.boolean().default(true),
  dealTitle: z.string().trim().min(2).max(200).optional(),
  dealValue: optionalMoney,
  expectedCloseAt: optionalDate,
});
export type ConvertLeadInput = z.infer<typeof ConvertLeadSchema>;

/* ==========================================================================
   الحسابات
   ========================================================================== */

export const AccountListQuerySchema = ListQuerySchema.extend({
  kind: z.enum(ACCOUNT_KINDS).optional(),
  industry: z.string().trim().max(80).optional(),
  ownerId: CuidSchema.optional(),
  isActive: z.coerce.boolean().optional(),
  /** دلو النظرة السريعة — انظر `ACCOUNT_BUCKETS`. */
  bucket: z.enum(ACCOUNT_BUCKETS).optional(),
});
export type AccountListQuery = z.infer<typeof AccountListQuerySchema>;

export const CreateAccountSchema = z.object({
  kind: z.enum(ACCOUNT_KINDS).default("COMPANY"),
  name: z.string().trim().min(2, "اسم الحساب مطلوب.").max(160),
  /* حرارة الاهتمام — كتصنيف العميل المحتمل سواء. */
  rating: z.enum(LEAD_RATINGS).optional(),
  legalName: optionalText(200),
  taxNumber: optionalText(40),
  email: optionalEmail,
  phone: optionalPhone,
  /* حقول ملف العميل المحتمل نفسها — نموذجٌ واحد للملفّين. */
  altPhone: optionalPhone,
  jobTitle: optionalText(120),
  messengerHandle: optionalText(200),
  instagramHandle: optionalText(200),
  waPhone: z.enum(["phone", "altPhone"]).optional(),
  smsPhone: z.enum(["phone", "altPhone"]).optional(),
  mapsUrl: optionalText(500),
  website: optionalText(200),
  industry: optionalText(80),
  companySize: z.enum(COMPANY_SIZES).nullable().optional(),
  city: optionalText(80),
  address: optionalText(300),
  notes: optionalText(2000),
  source: optionalText(80),
  ownerId: CuidSchema.nullable().optional(),
  creditLimit: optionalMoney,
  customFields: CustomFieldsBag.optional(),
  /* الصورة `data:` مُصغَّرة — الحدّ والصيغ في `avatar.ts`. */
  avatarUrl: AvatarFieldSchema,
});
export type CreateAccountInput = z.infer<typeof CreateAccountSchema>;

export const UpdateAccountSchema = CreateAccountSchema.partial().extend({
  isActive: z.boolean().optional(),
});
export type UpdateAccountInput = z.infer<typeof UpdateAccountSchema>;

export const ContactListQuerySchema = ListQuerySchema.extend({
  accountId: CuidSchema.optional(),
  /** جهات الاتصال الرئيسية وحدها. */
  primaryOnly: z.coerce.boolean().optional(),
  /** من له هاتف أو بريد فقط، لحملات التواصل. */
  reachable: z.coerce.boolean().optional(),
  /** دلو — يُصفّي بالمعيار نفسه الذي تُحسب به البطاقة. */
  bucket: z.enum(CONTACT_BUCKETS).optional(),
});
export type ContactListQuery = z.infer<typeof ContactListQuerySchema>;

export const CreateContactSchema = z.object({
  accountId: CuidSchema,
  name: z.string().trim().min(2, "الاسم مطلوب.").max(160),
  jobTitle: optionalText(120),
  email: optionalEmail,
  phone: optionalPhone,
  altPhone: optionalPhone,
  /* السوشيال وتوجيه القنوات — كملف العميل المحتمل سواء. */
  messengerHandle: optionalText(200),
  instagramHandle: optionalText(200),
  waPhone: z.enum(["phone", "altPhone"]).optional(),
  smsPhone: z.enum(["phone", "altPhone"]).optional(),
  isPrimary: z.boolean().default(false),
  notes: optionalText(1000),
  /*
   * المسؤول عن هذه الجهة تحديدًا.
   *
   * الغياب ليس نقصًا بل معنى: «مسؤولها هو مسؤول العميل». نسخُ مسؤول العميل
   * إليها عند الإنشاء كان سيفترق عنه عند أول نقل للعميل، فتصير جهات اتصاله
   * منسوبة إلى من لم يعد يتابعه.
   */
  ownerId: CuidSchema.nullable().optional(),
  /* الصورة `data:` مُصغَّرة — الحدّ والصيغ في `avatar.ts`. */
  avatarUrl: AvatarFieldSchema,
});
export type CreateContactInput = z.infer<typeof CreateContactSchema>;

export const UpdateContactSchema = CreateContactSchema.omit({
  accountId: true,
}).partial();
export type UpdateContactInput = z.infer<typeof UpdateContactSchema>;

/* ==========================================================================
   الصفقات
   ========================================================================== */

export const DealListQuerySchema = ListQuerySchema.extend({
  leadId: CuidSchema.optional(),
  stage: z.enum(DEAL_STAGES).optional(),
  result: z.enum(DEAL_RESULTS).optional(),
  accountId: CuidSchema.optional(),
  ownerId: CuidSchema.optional(),
  /** الصفقات المفتوحة فقط. */
  openOnly: z.coerce.boolean().optional(),
});
export type DealListQuery = z.infer<typeof DealListQuerySchema>;

/** الصفقةُ على حسابٍ أو على محتمل — أحدُهما، والخدمةُ تتحقّق. */
export const CreateDealSchema = z.object({
  accountId: CuidSchema.optional(),
  leadId: CuidSchema.optional(),
  contactId: CuidSchema.nullable().optional(),
  title: z.string().trim().min(2, "عنوان الصفقة مطلوب.").max(200),
  description: optionalText(2000),
  stage: z.enum(DEAL_STAGES).default("CONTACT"),
  value: money.default(0),
  /** يُشتق من المرحلة إن لم يُذكر. */
  probability: z.number().int().min(0).max(100).optional(),
  expectedCloseAt: optionalDate,
  ownerId: CuidSchema.nullable().optional(),
  source: optionalText(80),
});
export type CreateDealInput = z.infer<typeof CreateDealSchema>;

export const UpdateDealSchema = CreateDealSchema.partial().omit({
  accountId: true,
  leadId: true,
});
export type UpdateDealInput = z.infer<typeof UpdateDealSchema>;

export const CloseDealSchema = z
  .object({
    result: z.enum(["WON", "LOST"]),
    lostReason: optionalText(200),
    closedAt: optionalDate,
  })
  .refine((v) => v.result !== "LOST" || !!v.lostReason, {
    message: "سبب الخسارة مطلوب عند إغلاق الصفقة كخاسرة.",
    path: ["lostReason"],
  });
export type CloseDealInput = z.infer<typeof CloseDealSchema>;

/* ==========================================================================
   الأنشطة
   ========================================================================== */

export const ActivityListQuerySchema = ListQuerySchema.extend({
  type: z.enum(ACTIVITY_TYPES).optional(),
  status: z.enum(ACTIVITY_STATUSES).optional(),
  assigneeId: CuidSchema.optional(),
  leadId: CuidSchema.optional(),
  accountId: CuidSchema.optional(),
  dealId: CuidSchema.optional(),
  /** المستحقة اليوم أو قبله وغير المكتملة. */
  overdueOnly: z.coerce.boolean().optional(),
  /**
   * دلو زمني — انظر `ACTIVITY_BUCKETS`.
   *
   * يُصفّي بالمعيار نفسه الذي تُحسب به بطاقات النظرة السريعة، فما تفتحه
   * البطاقة بنقرة هو عين ما عدّته.
   */
  bucket: z.enum(ACTIVITY_BUCKETS).optional(),
  /**
   * مدى الموعد — به تُقرأ نافذةٌ زمنية بعينها.
   *
   * الدلاء تجيب عن «فات/اليوم/قادم» نسبةً إلى الآن، ولا تجيب عن «أرِني
   * أسبوع كذا». وتقويمُ الاجتماعات يتنقّل بين الأسابيع أمامًا وخلفًا،
   * فيحتاج مدًى صريحًا لا دلوًا. والحدّان شاملُ البداية دون النهاية،
   * فأسبوعان متتاليان لا يتقاسمان يومًا.
   */
  dueFrom: z.coerce.date().optional(),
  dueTo: z.coerce.date().optional(),
});
export type ActivityListQuery = z.infer<typeof ActivityListQuerySchema>;

const BaseActivitySchema = z.object({
  type: z.enum(ACTIVITY_TYPES),
  subject: z.string().trim().min(2, "الموضوع مطلوب.").max(200),
  body: optionalText(4000),
  status: z.enum(ACTIVITY_STATUSES).default("PLANNED"),
  priority: z.enum(PRIORITIES).default("NORMAL"),
  dueAt: optionalDate,
  durationMin: z.number().int().min(0).max(1440).nullable().optional(),
  assigneeId: CuidSchema.nullable().optional(),

  // خاص بالاجتماعات
  location: optionalText(200),
  meetingProvider: z.enum(MEETING_PROVIDERS).nullable().optional(),
  meetingUrl: optionalText(500),
  /** موقع الانعقاد على الخريطة — للحضوريّ يُملأ من ملف العميل ويقبل التعديل. */
  mapsUrl: optionalText(500),
  attendees: z.array(z.string().trim().max(200)).max(50).default([]),

  // خاص بالمكالمات
  callDirection: z.enum(CALL_DIRECTIONS).nullable().optional(),
  callOutcome: optionalText(200),

  // خاص بالرسائل
  mailTo: optionalText(300),
  mailCc: optionalText(300),
  mailSubject: optionalText(200),
  messageChannel: z.enum(MESSAGE_CHANNELS).nullable().optional(),

  // خاص بالملاحظات: أيَّ جانبٍ تخصّ الملاحظة — والفارغ ملاحظةٌ عامّة
  noteAbout: z.enum(["TASK", "MEETING", "CALL", "MAIL"]).nullable().optional(),

  // خاص بالاجتماعات: تسجيل الفيديو — يُلحق بعد الإنجاز بطبيعته.
  // يقبل رابطًا خارجيًّا (زوم/درايف…) أو معرّفَ مرفقٍ داخليٍّ لتسجيلِ Jitsi
  // المرفوعِ إلى النظام (سلسلةٌ من حروفٍ وأرقامٍ بطول 20–32).
  recordingUrl: z
    .string()
    .trim()
    .max(500)
    .refine((v) => /^https?:\/\//.test(v) || /^[a-z0-9]{20,32}$/i.test(v), "رابط التسجيل غير صالح")
    .nullable()
    .optional(),

  // الارتباط بسجل واحد على الأكثر من كل نوع
  leadId: CuidSchema.nullable().optional(),
  accountId: CuidSchema.nullable().optional(),
  contactId: CuidSchema.nullable().optional(),
  dealId: CuidSchema.nullable().optional(),
});

/**
 * النشاط يجب أن يرتبط بسجل واحد على الأقل.
 *
 * نشاط بلا ارتباط لا يظهر في تاريخ أي عميل، فيضيع الغرض منه: أن يكون سجل
 * التعامل مع العميل مجتمعًا في مكان واحد.
 */
export const CreateActivitySchema = BaseActivitySchema.refine(
  (v) => !!(v.leadId || v.accountId || v.dealId || v.contactId),
  {
    message: "يجب ربط النشاط بعميل محتمل أو حساب أو صفقة.",
    path: ["accountId"],
  },
);
export type CreateActivityInput = z.infer<typeof CreateActivitySchema>;

export const UpdateActivitySchema = BaseActivitySchema.partial();
export type UpdateActivityInput = z.infer<typeof UpdateActivitySchema>;

export const CompleteActivitySchema = z.object({
  outcome: optionalText(500),
  completedAt: optionalDate,
  /** إنشاء نشاط متابعة مباشرة بعد إكمال هذا. */
  followUp: BaseActivitySchema.partial().optional(),
});
export type CompleteActivityInput = z.infer<typeof CompleteActivitySchema>;
